import { createCustomTheme } from '@domains/subscription/theme'
import { ThemeProvider } from '@mui/material'
import { isAxiosError } from 'axios'
import useMaterialNotification from 'hooks/useMaterialNotification'
import type { Amount } from '../api/api'
import {
  useAllTemplates,
  useCustomerTemplate,
  useDeleteSubscriptionFee,
  useSetCustomerTemplate,
  useSetSubscriptionFee,
  useSubscriptionFee,
  useTemplateRelations,
} from '../api/queries'
import { CustomerSubscriptionPlan, type CustomerSubscriptionPlanProps } from '../usecases/CustomerSubscriptionPlan'

interface CustomerSubscriptionPlanFactoryProps {
  customerId: string
  onChange?: () => void
}

export const CustomerSubscriptionPlanFactory = ({ customerId, onChange }: CustomerSubscriptionPlanFactoryProps) => {
  const {
    data: customerTemplate,
    isLoading: isCustomerTemplateLoading,
    isFetching: isCustomerTemplateFetching,
    isError: isErrorCustomerTemplate,
    failureReason: customerTemplateError,
  } = useCustomerTemplate(customerId)
  const { data: templateRelations = [], isLoading: _isTemplateRelationsLoading } = useTemplateRelations(customerTemplate?.id)
  const { data: allTemplates = [], isLoading: isAllTemplateRelationsLoading } = useAllTemplates()
  const { mutate: setCustomerTemplate, isLoading: isPatchingCustomerTemplate } = useSetCustomerTemplate(customerId)
  const { data: subscriptionFee, isLoading: isSubscriptionFeeLoading } = useSubscriptionFee(customerId)
  const { mutate: setSubscriptionFee, isLoading: isPatchingSubscriptionFee } = useSetSubscriptionFee(customerId)
  const { mutate: deleteSubscriptionFee, isLoading: isDeletingSubscriptionFee } = useDeleteSubscriptionFee(customerId)

  const notify = useMaterialNotification()

  const handleChangeTemplate: CustomerSubscriptionPlanProps['onSelectTemplate'] = (options) => {
    setCustomerTemplate(options, {
      onSuccess: () => {
        notify('Subscription plan updated')

        onChange?.()
      },
    })
  }

  const handleChangeSubscriptionFee = (fee: Amount) => {
    setSubscriptionFee(fee, {
      onSuccess: () => {
        notify('Subscription fee updated')

        onChange?.()
      },
    })
  }

  const handleDeleteSubscriptionFee = () => {
    deleteSubscriptionFee(undefined, {
      onSuccess: () => {
        notify('Subscription fee override deleted')

        onChange?.()
      },
    })
  }

  const isError = isAxiosError(customerTemplateError) ? customerTemplateError.response?.status !== 404 : isErrorCustomerTemplate

  const isTemplateRelationsLoading = customerTemplate?.id ? _isTemplateRelationsLoading : false

  const isLoading = isCustomerTemplateLoading || isTemplateRelationsLoading || isAllTemplateRelationsLoading || isSubscriptionFeeLoading
  const isWaitingForPatchResult = isPatchingCustomerTemplate || isCustomerTemplateFetching || isPatchingSubscriptionFee

  const avaliableTemplates = templateRelations.flatMap((relation) => relation.relatives)

  return (
    <ThemeProvider theme={createCustomTheme()}>
      <CustomerSubscriptionPlan
        onDeleteSubscriptionFee={handleDeleteSubscriptionFee}
        subscriptionFee={subscriptionFee}
        onSelectTemplate={handleChangeTemplate}
        onSetSubscriptionFee={handleChangeSubscriptionFee}
        allTemplates={allTemplates}
        templateRelations={avaliableTemplates}
        customerTemplate={customerTemplate}
        isLoading={isLoading}
        isPatching={isWaitingForPatchResult}
        isError={isError}
        isDeleting={isDeletingSubscriptionFee}
      />
    </ThemeProvider>
  )
}
